package com.advaita.cloud.drive.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.GivenShareDto
import com.advaita.cloud.drive.ui.api.AuthApi
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.components.AdmissionPage
import com.advaita.cloud.drive.ui.components.Breadcrumb
import com.advaita.cloud.drive.ui.components.admissionHoldOf
import com.advaita.cloud.drive.ui.components.FileTable
import com.advaita.cloud.drive.ui.components.Header
import com.advaita.cloud.drive.ui.components.LoginGate
import com.advaita.cloud.drive.ui.components.Modal
import com.advaita.cloud.drive.ui.components.PreviewPane
import com.advaita.cloud.drive.ui.components.PreviewSplitter
import com.advaita.cloud.drive.ui.components.PreviewTooltip
import com.advaita.cloud.drive.ui.components.SearchModal
import com.advaita.cloud.drive.ui.components.BulkDeleteProgress
import com.advaita.cloud.drive.ui.components.CappedNotice
import com.advaita.cloud.drive.ui.components.SelectionBar
import com.advaita.cloud.drive.ui.components.SharedBreadcrumb
import com.advaita.cloud.drive.ui.components.SharedByMeView
import com.advaita.cloud.drive.ui.components.ViewTabs
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.ShareModal
import com.advaita.cloud.drive.ui.components.capSentence
import com.advaita.cloud.drive.ui.components.SigningInGate
import com.advaita.cloud.drive.ui.components.ToastStack
import com.advaita.cloud.drive.ui.components.UploadProgress
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import com.advaita.cloud.drive.ui.store.PreviewStore
import com.advaita.cloud.drive.ui.store.PreviewTooltipStore
import com.advaita.cloud.drive.ui.store.SearchStore
import com.advaita.cloud.drive.ui.store.DriveView
import com.advaita.cloud.drive.ui.store.ShareDialogStore
import com.advaita.cloud.drive.ui.store.SharedWithMeStore
import com.advaita.cloud.drive.ui.store.SharedByMeStore
import com.advaita.cloud.drive.ui.store.MyAccessStore
import com.advaita.cloud.drive.ui.store.stopPrompt
import com.advaita.cloud.drive.ui.store.leavePrompt
import com.advaita.cloud.drive.ui.store.sortedForTable
import com.advaita.cloud.drive.ui.components.ToastLevel
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun App(
    store: FileBrowserStore,
    preview: PreviewStore,
    pickerPeek: PreviewTooltipStore,
    search: SearchStore,
    share: ShareDialogStore,
    sharedWithMe: SharedWithMeStore,
    sharedByMe: SharedByMeStore,
    myAccess: MyAccessStore,
) {
    // ADV-1155: checked before the token, because on this path the token has just been cleared
    // and LoginGate would claim the user is simply signed out mid-navigation.
    if (store.signingIn) {
        SigningInGate()
        return
    }
    val tokenPresent = AuthApi.currentToken() != null
    if (!tokenPresent) {
        LoginGate(loopDetected = AuthApi.loginLoopDetected())
        return
    }
    LaunchedEffect(Unit) {
        // ADV-1155: the loaders rethrow RedirectingException now, so the first 401 aborts this
        // sequence instead of firing two more doomed fetches. Catch it HERE: a LaunchedEffect
        // runs on the composition's coroutine context, and letting the throw escape would
        // cancel the recomposer — a frozen page instead of a redirect.
        //
        // signInStarted() is not bookkeeping — it is the only thing that changes Compose state
        // on this path, so without it nothing recomposes and the UI is stuck on "Loading…"
        // until the navigation commits. It might never commit.
        try {
            store.loadCurrentUser()
            // ADV-1326c (RULING 2026-09-28 (1)): /api/me answered admission null, so ask
            // /api/me/admission once, about 2 s from now. It runs beside the listing and never
            // delays it. A hold in the answer switches the page below to the hold page.
            launch {
                try {
                    store.pollAdmissionOnce()
                } catch (e: RedirectingException) {
                    console.log("[drive] admission poll aborted, signing in: ${e.message}")
                    store.signInStarted()
                }
            }
            // Before the hold check: Sign out on a hold page needs az2LogoutUrl to end the az2
            // session too (ADV-1326c review).
            store.loadAuthConfig()
            // ADV-1326c: a person held on the Pending or no-access page lists nothing.
            if (admissionHoldOf(store.currentUser) != null) return@LaunchedEffect
            // ADV-1330 F3: a deep link to /ui/?view=shared opens on Shared with me and skips My
            // Drive's listing. With the share UI hidden (ruling item 1), it lands on My Drive.
            if (store.view == DriveView.SHARED && store.shareUiVisible) sharedWithMe.load()
            else if (store.view == DriveView.SHARED_BY_ME && store.shareUiVisible) sharedByMe.load()
            else if (store.view == DriveView.SHARED || store.view == DriveView.SHARED_BY_ME) store.landOnMyDrive()
            else store.refresh()
        } catch (e: RedirectingException) {
            console.log("[drive] startup aborted, signing in: ${e.message}")
            store.signInStarted()
        }
    }
    // A refresh may have removed the previewed row (deleted, or a folder change),
    // and a pane left open on it would offer a download that 404s.
    //
    // In a SideEffect, not in the composition body: reconcile WRITES snapshot
    // state (the selection, the descriptor, the content), and writing state
    // that this composition also reads is how a recomposition loop starts. A
    // SideEffect runs after a successful composition, which is the earliest
    // point at which publishing to a non-Compose object is safe.
    //
    // The list is read HERE and captured, not read inside the lambda. A
    // SideEffect body runs outside composition, so a read in there subscribes
    // nothing — and the only other read is inside a Div content lambda, which
    // is its own recompose scope. App would then never recompose on a new
    // listing, the effect would never re-run, and a pane left open on a row
    // that is no longer in view would keep drawing it.
    val inShared = store.view == DriveView.SHARED
    // CR-6.3: Shared by me, a flat list of the caller's grants (no folder, no preview).
    val inSharedByMe = store.view == DriveView.SHARED_BY_ME
    val files = if (inShared) sharedWithMe.items else store.files
    SideEffect { preview.reconcile(files) }
    // A dialog is open: two preview surfaces on screen at once read as clutter,
    // and the docked pane sat half-dimmed behind the backdrop with no way to
    // reach it. The store keeps its selection, so it returns on close.
    val modalOpen = store.activeModal != null || share.isOpen
    // ADV-1330: the session's own share cap (S8 rows 1, 5, 9) caps every row's Share action.
    val sessionShareCap = store.sessionShareCap
    // ADV-1330 (the frontend build ruling, item 1): no Share control and no Shared with me tab
    // until /api/me says sharingEnabled.
    val shareUi = store.shareUiVisible
    // ADV-1326c: /api/me says PENDING, DENIED or REMOVED. The header keeps the identity and Sign
    // out, and nothing that creates. Every other answer, null included, keeps the page as it was
    // before admission.
    val hold = admissionHoldOf(store.currentUser)
    Header(store, held = hold != null, access = myAccess)
    hold?.let {
        AdmissionPage(it)
        return
    }
    // ADV-1330 F3: My Drive or Shared with me. Shared with me is read on every open, so a
    // share made a moment ago shows without a reload.
    if (shareUi) ViewTabs(
        view = store.view,
        onMyDrive = { store.navigateTo(store.currentPath) },
        onShared = {
            store.showSharedWithMe()
            MainScope().launch { sharedWithMe.load() }
        },
        onSharedByMe = {
            store.showSharedByMe()
            MainScope().launch { sharedByMe.load() }
        },
    )
    if (inSharedByMe) {
        // Shared by me is a flat list: it has no folder path.
    } else if (inShared) {
        // ADV-1330 (Tim): the search icon inside a shared folder searches that folder. The roots
        // list has none: its rows belong to several owners, and no route searches them together.
        val openShared = sharedWithMe.currentFolder
        SharedBreadcrumb(
            trail = sharedWithMe.trail.map { it.displayName },
            onNavigate = { depth -> MainScope().launch { sharedWithMe.goToDepth(depth) } },
            onSearchClick = openShared?.let { f -> { search.openShared(f, root = sharedWithMe.trail.first()) } },
        )
    } else {
        Breadcrumb(
            path = store.currentPath,
            onNavigate = { path -> store.navigateTo(path) },
            onSearchClick = { search.open(store.currentPath) },
        )
    }
    UploadProgress(store)
    BulkDeleteProgress(store)
    // One flag drives the whole archive surface: no selection bar, no checkbox column,
    // no 📂 action when the backend reports the feature off.
    val archiveEnabled = store.authConfig?.archiveEnabled == true
    // Tim (2026-09-28): the bar shows in Shared with me too, the same as in My Drive.
    if (archiveEnabled && !inSharedByMe) SelectionBar(store)
    // .workspace is OUTSIDE the when, so only the table half is swapped for a
    // status. Inside it, every folder navigation unmounted the docked pane
    // through the isLoading branch and remounted it empty — the flicker the
    // pane exists to avoid. The e2e test missed it because Playwright polls
    // until loading has already resolved.
    //
    // The pane is a SIBLING of the table inside .workspace, so ADV-1199's
    // folder tree can be added to the left of both without touching either.
    Div(attrs = { classes("workspace") }) {
        when {
            inSharedByMe && shareUi -> SharedByMeView(
                shared = sharedByMe,
                onOpen = { row -> openGiven(store, sharedWithMe, row) },
                onStop = { row -> stopGiven(store, sharedByMe, row) },
            )
            inSharedByMe -> Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            inShared && shareUi -> SharedWithMeView(store, sharedWithMe, preview, share, sessionShareCap)
            // A ?view=shared link before /api/me answers, or with sharing off: nothing yet.
            inShared -> Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            store.isLoading -> Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            store.error != null -> Div(attrs = { classes("status-pad", "error") }) {
                Text(store.error ?: "Unknown error")
            }
            store.notFound -> Div(attrs = { classes("status-pad") }) {
                Text("This folder doesn't exist. Click \"My Drive\" above to go home.")
            }
            else -> FileTable(
                files = store.sortedFiles,
                selectedFileId = preview.selectedFileId,
                onPreview = { file -> preview.openFile(file) },
                onFolderClick = { file -> store.navigateTo(file.logicalPath) },
                onDownload = { file -> store.download(file) },
                sortKey = store.sortKey,
                sortAscending = store.sortAscending,
                onSortClick = store::onSortHeaderClick,
                highlightId = store.pendingHighlightId,
                onHighlightShown = store::highlightConsumed,
                archiveEnabled = archiveEnabled,
                selectedIds = store.selectedIds,
                // ADV-1465: the range must use the rows as the table renders them.
                onToggleSelected = { file, shift -> store.clickSelect(file.id, shift, store.sortedFiles.map { it.id }) },
                onToggleSelectAllVisible = { store.toggleSelectAllVisible() },
                onExtract = { file -> store.extract(file) },
                extractingIds = store.extractingIds,
                onShare = if (store.shareControlVisible) { file -> share.open(file, sessionShareCap) } else null,
                shareCapFor = { sessionShareCap?.let { capSentence(it) } },
                onDelete = { file -> confirmAndDelete(store, file) },
            )
        }
        if (!modalOpen && !inSharedByMe) {
            PreviewSplitter(preview)
            PreviewPane(preview)
        }
    }
    // The share dialog first: its Leave confirm is a Modal, and the later node draws on top.
    ShareModal(share)
    Modal(store, pickerPeek)
    // Outside Modal's DOM on purpose: `.modal` animates transform: scale(), which
    // makes it a containing block for a position:fixed child while that runs.
    PreviewTooltip(pickerPeek)
    ToastStack(store)
    // ADV-1215 spotlight search. A result click reveals the target folder and
    // flashes the picked file the same way FileTable's own highlight does.
    SearchModal(search) { picked ->
        store.pickSearchResult(search, picked)
        search.close()
    }
}

/**
 * ADV-1330 F3 (ruling S7): the Shared with me list. The sharer and the level show on each shared
 * root, with Leave on the caller's own grant. An outage is a banner, never an empty list, and
 * with the checks off the S8 row 1 sentence takes the list's place.
 */
@Composable
private fun SharedWithMeView(
    store: FileBrowserStore,
    shared: SharedWithMeStore,
    preview: PreviewStore,
    share: ShareDialogStore,
    sessionShareCap: ShareCap?,
) {
    Div(attrs = { classes("shared-view") }) {
        shared.bannerSentence?.let { Div(attrs = { classes("shared-banner"); attr("role", "status") }) { Text(it) } }
        when {
            shared.loading -> Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            shared.listCap != null -> CappedNotice(shared.listCap!!)
            shared.error != null -> Div(attrs = { classes("status-pad", "error") }) { Text(shared.error!!) }
            shared.showTable -> run {
                // ADV-1465: computed once, so the table's rendered rows and the Shift+click
                // range walk the same order.
                val sharedRows = sortedForTable(shared.items, store.sortKey, store.sortAscending)
                FileTable(
                    files = sharedRows,
                    selectedFileId = preview.selectedFileId,
                    onPreview = { file -> preview.openFile(file) },
                    onFolderClick = { file -> MainScope().launch { shared.openFolder(file) } },
                    onDownload = { file -> store.download(file) },
                    // Tim's rig screens: the row actions follow the caller's grants on the root, where
                    // the backend would accept them (SharedWithMeStore.rowActions). The selection column
                    // shows as in My Drive, with Shared with me's own selection.
                    onDelete = { file -> confirmAndDelete(store, file) },
                    deleteVisible = { file -> shared.rowActions(file).delete },
                    archiveEnabled = store.authConfig?.archiveEnabled == true,
                    onExtract = { file -> store.extract(file) },
                    extractingIds = store.extractingIds,
                    extractVisible = { file -> shared.rowActions(file).extract },
                    sortKey = store.sortKey,
                    sortAscending = store.sortAscending,
                    onSortClick = store::onSortHeaderClick,
                    highlightId = store.pendingHighlightId,
                    onHighlightShown = store::highlightConsumed,
                    readOnly = !store.sharedSelectionShown(store.authConfig?.archiveEnabled == true),
                    selectedIds = shared.selectedIds,
                    // ADV-1465: the range must use the rows as the table renders them.
                    onToggleSelected = { file, shift -> shared.clickSelect(file.id, shift, sharedRows.map { it.id }) },
                    onToggleSelectAllVisible = { shared.toggleSelectAllVisible() },
                    sharedInfo = if (shared.shareColumns) { file -> shared.rowInfo(file) } else null,
                    onLeave = { file -> leaveShared(store, shared, file) },
                    // Share only where the caller's own grant holds `share`. Else the dialog could
                    // only answer with a refusal, and "Your organization does not allow this action."
                    // would be the wrong reason for a viewer.
                    onShare = if (store.shareControlVisible) { file -> share.open(file, sessionShareCap) } else null,
                    shareVisible = { file -> shared.canShare(file) },
                    shareCapFor = { sessionShareCap?.let { capSentence(it) } },
                    emptyText = store.sharedEmptyText,
                )
            }
        }
        if (shared.truncated && shared.trail.isEmpty()) {
            Div(attrs = { classes("status-pad") }) { Text("Showing the first part of a long list.") }
        }
    }
}

/** Delete with the confirm the row's kind needs ([FileBrowserStore.confirmDelete]). */
private fun confirmAndDelete(store: FileBrowserStore, file: com.advaita.cloud.drive.shared.FileDto) {
    MainScope().launch { store.confirmDelete(file) }
}

private fun leaveShared(store: FileBrowserStore, shared: SharedWithMeStore, file: com.advaita.cloud.drive.shared.FileDto) {
    MainScope().launch {
        val prompt = leavePrompt(file.displayName)
        val ok = store.confirm(title = prompt.title, message = prompt.message, confirmLabel = "Leave")
        if (!ok) return@launch
        val refusal = shared.leave(file)
        if (refusal == null) store.toast(prompt.done, ToastLevel.SUCCESS)
        else store.toast(refusal, ToastLevel.INFO)
    }
}

/**
 * Open on Shared by me. DataHub has no link by file id. An item that the caller owns opens in its
 * folder in My Drive and flashes. An item that another person owns opens Shared with me.
 */
private fun openGiven(store: FileBrowserStore, sharedWithMe: SharedWithMeStore, row: GivenShareDto) {
    val parent = row.itemParentPath
    if (parent != null) {
        store.revealFile(parent, row.itemId)
    } else {
        store.showSharedWithMe()
        MainScope().launch { sharedWithMe.load() }
    }
}

/** Stop sharing asks first, because the vault also ends the shares that the recipient made from this one. */
private fun stopGiven(store: FileBrowserStore, shared: SharedByMeStore, row: GivenShareDto) {
    MainScope().launch {
        val prompt = stopPrompt(shared.itemLabel(row), shared.recipientLabel(row))
        val ok = store.confirm(title = prompt.title, message = prompt.message, confirmLabel = "Stop sharing", destructive = true)
        if (!ok) return@launch
        val refusal = shared.stop(row)
        if (refusal == null) store.toast(prompt.done, ToastLevel.SUCCESS)
        else store.toast(refusal, ToastLevel.INFO)
    }
}
